// ══════════════ POINTAGE TERRAIN (journalier + par appartement) ══════════════
// Extrait de GerantClientUI.jsx (refacto étape B — briques terrain, 07/07/2026).
// DailyPunch et AptPunch sont utilisés ensemble dans l'onglet "ptg" de StaffUI.
// Feuilles : aucune dépendance vers un composant interne encore non extrait de
// GerantClientUI (CommentaireModal, AlerteSanteButton, etc. — appelés via callbacks
// props onComment/onRep, jamais référencés directement ici).
// Ne pas confondre avec src/components/Pointage.jsx (éditeurs admin PointageTcEditor/
// PointageAlsCreator/PointageAlsEditor + hook useGeo) : ce fichier est le pointage
// terrain salarié, l'autre est l'administration des pointages.
import React, { useState, useEffect, useRef } from 'react';
import { today, breakInProgress, pauseStatus, now, totalWorkMs, getBreaks, fdur, ft, totalBreakMs, geoOk, fname, fnameShort, guideImposition, calcRapportsEnAttente, fermerPauseOuverte, applyTcUpdate, dureeStatut } from "../utils.js";
import { useGeo } from "./Pointage.jsx";
import { supaGet, supaSet, supaSetSafe, syncBus } from "../supabase.js";
import { notifEventBus } from '../notifBus.js';
import { TT } from "../constants.js";
import { Ic } from "./Ic.jsx";
import { M } from "./M.jsx";
import { FichePrepaModal } from "./SecPrest.jsx";
import { tr, ClotureJournee, clotureCode, detectPauseOubliee, PauseOubliee } from "./EncadrementRenforce.jsx";
import { listAll, enqueue, confirmByRecordIds } from "../localQueue.js";
import { FinJourneeCheck } from "./FinJourneeCheck.jsx";

function DailyPunch({sid,tc,setTc,setTcSynced,encadre=false,langue='fr',clotureBlockers=[],authorName='',biens=[],aptAnomalies=[],onFinJourneeLog}){
  const todayS=today();
  const clk=tc.find(c=>c.sid===sid&&c.date===todayS);
  const{req}=useGeo();
  const[cf,setCf]=useState(null);
  const[ld,setLd]=useState(null);
  // Encadrement renforcé (Lot C) : écran de clôture (check-list bloquante) + reçu.
  const[blocked,setBlocked]=useState(false);
  const[receipt,setReceipt]=useState(false);
  // Lot 2 (SPEC_file_locale_v2.md) : écran de fin de journée pour les salariés
  // SANS encadrementRenforce. finJ=null tant que rien n'a été détecté ou vérifié.
  const[finJ,setFinJ]=useState(null); // {rapports,apparts} une fois une anomalie détectée
  const[finJSent,setFinJSent]=useState(false);
  const[checkingFinJ,setCheckingFinJ]=useState(false);
  // Timer pour réévaluer le statut de pause toutes les minutes (sinon la bannière n'apparaît qu'au prochain pointage)
  const[,setNowTick]=useState(0);
  useEffect(()=>{const id=setInterval(()=>setNowTick(t=>t+1),60000);return()=>clearInterval(id);},[]);
  // État de la machine : où en est-on dans la journée ?
  // - pas commencé → bouton "Début de poste"
  // - en travail → boutons "Début de pause" et "Fin de poste"
  // - en pause → bouton "Fin de pause"
  // - terminé → tout grisé
  const started=!!clk?.sw;
  const ended=!!clk?.ew;
  const onBreak=breakInProgress(clk);
  // Évaluation du statut réglementaire des pauses (uniquement en cours de journée)
  const pauseSt=started&&!ended?pauseStatus(clk):null;
  const punch=k=>{setLd(k);req((g,err)=>{setLd(null);setCf({k,g:g||null,geoErr:err||null});});};
  // tc7 (v134) : lecture fraîche juste avant écriture — un seul enregistrement par
  // salarié-jour, modifié en place, contrairement à als7/controles7 (événements
  // atomiques). Sans ça, une écriture concurrente (autre onglet, correction admin
  // pendant que l'app était fermée) est silencieusement écrasée. En cas d'échec
  // réseau, repli sur le setTc classique — useSt reprend le retry avec backoff.
  const applyPunch=(k,ts,g,geoErr)=>c=>{
    if(k==='sw')return{...c,sw:{ts,geo:g,geoErr}};
    if(k==='ew')return{...c,ew:{ts,geo:g,geoErr}};
    if(k==='sb'){
      // Démarrer une nouvelle pause : on l'ajoute au tableau `breaks` (source unique de vérité)
      const newBreak={start:ts,end:null,geo:g,geoErr};
      return{...c,breaks:[...(c.breaks||[]),newBreak]};
    }
    if(k==='eb'){
      // Terminer la pause en cours
      const breaks=[...(c.breaks||[])];
      const last=breaks.length-1;
      if(last>=0&&!breaks[last].end)breaks[last]={...breaks[last],end:ts,geoEnd:g,geoErrEnd:geoErr};
      return{...c,breaks};
    }
    return c;
  };
  const confirm=async()=>{
    const{k,g,geoErr}=cf;const ts=now();
    const makeNew=()=>({id:'tc'+Date.now(),sid,date:todayS,sw:{ts,geo:g,geoErr},breaks:[]});
    const update=applyPunch(k,ts,g,geoErr);
    try{
      const{merged}=await supaSetSafe('tc7',fresh=>applyTcUpdate(fresh,sid,todayS,{makeNew,update}));
      if(setTcSynced)setTcSynced(merged);
    }catch(e){
      console.error('[tc7] confirm() écriture sûre échouée, repli local :',e.message);
      if(!clk)setTc([...tc,makeNew()]);
      else if(setTc)setTc(tc.map(c=>c.id===clk.id?update(c):c));
    }
    // Encadrement renforcé : à la fin de poste, reçu de clôture + notif Direction
    // (code déterministe, non inventable, recalculable par la Direction).
    if(k==='ew'&&encadre){
      const code=clotureCode(sid,todayS);
      try{
        notifEventBus.emit('cloture_attestee',{
          fromName:authorName||'Salarié(e)',
          code,
          summary:`✅ ${authorName||'Un salarié'} a clôturé sa journée à ${ft(ts)} — code ${code}`,
        });
      }catch(_){/* jamais bloquant */}
      setReceipt(true);
    }
    setCf(null);
  };
  // ─── Lot 2 : vérification de fin de journée (hors encadrement renforcé) ───
  // Combine deux sources : la file locale du lot 1 (rapports 'fin' du jour
  // encore pending/failed) et aptAnomalies (appartements du jour non pointés,
  // précalculé par StaffUI). Rien à signaler → log silencieux + punch normal.
  // Anomalie → écran bloquant ; la seule action possible est de prévenir la
  // Direction (voir SPEC §2 déc. #4), après quoi le punch se poursuit.
  const buildFinJEntry=(action,detail)=>({
    id:'j'+Date.now()+Math.random().toString(36).slice(2,6),
    ts:new Date().toISOString(),
    qui:authorName||'Salarié(e)',
    role:'salarie',
    sid,
    action,
    objet:'jour',
    cible:todayS,
    detail,
    ...(action==='fin_journee_anomalie'?{traite:false,traitePar:null,traiteTs:null}:{}),
  });
  const checkFinJournee=async()=>{
    setCheckingFinJ(true);
    let rapportsAnos=[];
    try{
      const entries=await listAll();
      rapportsAnos=calcRapportsEnAttente(entries,sid,todayS)
        .map(e=>({uid:e.uid,bienName:(biens.find(b=>b.id===e.payload?.bid)?.name)||'?',since:ft(e.createdAt)}));
    }catch(err){console.error('[FinJournee] lecture file locale échouée (non bloquant)',err);}
    setCheckingFinJ(false);
    const apparts=aptAnomalies||[];
    if(rapportsAnos.length===0&&apparts.length===0){
      if(onFinJourneeLog)onFinJourneeLog(buildFinJEntry('fin_journee_ok','Aucune anomalie'));
      punch('ew');
      return;
    }
    setFinJ({rapports:rapportsAnos,apparts});
  };
  const envoyerFinJ=()=>{
    const{rapports,apparts}=finJ;
    try{
      notifEventBus.emit('fin_journee_anomalie',{
        fromName:authorName||'Salarié(e)',
        summary:`📋 ${authorName||'Un salarié'} a terminé sa journée avec ${rapports.length+apparts.length} élément(s) en suspens`,
      });
    }catch(_){/* jamais bloquant */}
    const detailParts=[];
    if(rapports.length)detailParts.push(`${rapports.length} rapport(s) en attente : ${rapports.map(r=>r.bienName).join(', ')}`);
    if(apparts.length)detailParts.push(`${apparts.length} appartement(s) non pointé(s) : ${apparts.map(a=>a.bienName).join(', ')}`);
    if(onFinJourneeLog)onFinJourneeLog(buildFinJEntry('fin_journee_anomalie',detailParts.join(' — ')));
    setFinJSent(true);
  };
  const fermerFinJ=()=>{setFinJ(null);setFinJSent(false);punch('ew');};
  const wMs=totalWorkMs(clk);
  const breaks=getBreaks(clk);
  return(<div>
    {/* Bannière réglementaire : rappel de pause après 5h30 / 6h sans pause valide (≥30 min) */}
    {pauseSt&&(pauseSt.status==='warning'||pauseSt.status==='overdue')&&(
      <div style={{background:pauseSt.status==='overdue'?'#fde0e0':'#fff4d6',border:`2px solid ${pauseSt.status==='overdue'?'var(--dg)':'#e5c977'}`,borderRadius:12,padding:'14px 18px',marginBottom:14,display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
        <div style={{fontSize:32}}>{pauseSt.status==='overdue'?'🚨':'⏰'}</div>
        <div style={{flex:1,minWidth:200}}>
          <div className="D" style={{fontSize:15,color:pauseSt.status==='overdue'?'var(--dg)':'#7a5a00',marginBottom:2}}>
            {pauseSt.status==='overdue'?'Pause obligatoire dépassée !':'Pause réglementaire à prendre'}
          </div>
          <div style={{fontSize:12,color:pauseSt.status==='overdue'?'#8a0000':'#7a5a00'}}>
            Vous travaillez depuis <strong>{fdur(pauseSt.workMs)}</strong>{pauseSt.status==='overdue'?' sans pause de 30 min minimum. Le Code du travail vous impose une pause après 6h de travail.':'. Avant 6h de travail, vous devez prendre une pause d\'au moins 30 minutes.'}
          </div>
        </div>
        <button onClick={()=>!onBreak&&!ended&&punch('sb')} className="btn bor sm" disabled={onBreak||ended||ld==='sb'} style={{fontSize:13,fontWeight:700}}>☕ Commencer ma pause</button>
      </div>
    )}
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10,marginBottom:14}}>
      <div className="D" style={{fontSize:17}}>Pointage journalier</div>
      {wMs&&<div style={{background:'#e1f7ee',border:'1.5px solid var(--mn)',borderRadius:9,padding:'7px 12px'}}><div style={{fontSize:10,textTransform:'uppercase',color:'var(--si)'}}>Temps travaillé</div><div className="D" style={{fontSize:20,color:'var(--ok)'}}>{fdur(wMs)}</div></div>}
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,marginBottom:16}}>
      {/* Début de poste */}
      <button onClick={()=>!started&&punch('sw')} className={`pbtn ps ${started?'done':''}`} disabled={started||ld==='sw'}>
        <div style={{fontSize:24}}>{ld==='sw'?'⏳':started?'✅':'🟢'}</div>
        <div style={{fontSize:12,fontWeight:700}}>Début de poste</div>
        {clk?.sw&&<div style={{fontSize:10,opacity:.8}}>{ft(clk.sw.ts)}</div>}
      </button>
      {/* Pause : bouton qui bascule selon l'état (Début/Fin de pause) */}
      {!onBreak?
        <button onClick={()=>started&&!ended&&punch('sb')} className="pbtn pbs" disabled={!started||ended||ld==='sb'}>
          <div style={{fontSize:24}}>{ld==='sb'?'⏳':'☕'}</div>
          <div style={{fontSize:12,fontWeight:700}}>Début de pause</div>
          {breaks.length>0&&<div style={{fontSize:10,opacity:.8}}>{breaks.length} pause{breaks.length>1?'s':''} déjà</div>}
        </button>
        :
        <button onClick={()=>punch('eb')} className="pbtn pbe" disabled={ld==='eb'} style={{borderColor:'var(--or)',background:'#fff4e1'}}>
          <div style={{fontSize:24}}>{ld==='eb'?'⏳':'🔄'}</div>
          <div style={{fontSize:12,fontWeight:700}}>Fin de pause</div>
          <div style={{fontSize:10,opacity:.8,color:'var(--or)'}}>en pause depuis {ft(breaks[breaks.length-1]?.start)}</div>
        </button>
      }
      {/* Récap pauses (3ème case visuelle si plusieurs pauses) */}
      <div style={{gridColumn:'span 1',background:breaks.length>1?'#fff8e1':'var(--bg)',borderRadius:14,padding:'12px 14px',border:'1px solid var(--li)',minHeight:95,display:'flex',flexDirection:'column',justifyContent:'center'}}>
        <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--si)',fontWeight:700,marginBottom:6}}>Pauses du jour</div>
        {breaks.length===0?<div style={{fontSize:12,color:'var(--si)'}}>Aucune pause</div>:(
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            {breaks.map((b,i)=>(<div key={i} style={{fontSize:11,fontWeight:600}}>
              ☕ {ft(b.start)} → {b.end?ft(b.end):<span style={{color:'var(--or)'}}>en cours…</span>}
              {b._admin&&<span style={{marginLeft:5,fontSize:9,color:'var(--or)'}}>✎</span>}
            </div>))}
          </div>
        )}
      </div>
      {/* Fin de poste */}
      <button onClick={()=>{
        if(!started||onBreak||ended)return;
        if(encadre){
          if((clotureBlockers||[]).length>0){setBlocked(true);}else{punch('ew');}
          return;
        }
        checkFinJournee();
      }} className={`pbtn pe ${ended?'done':''}`} disabled={!started||onBreak||ended||ld==='ew'||checkingFinJ}>
        <div style={{fontSize:24}}>{(ld==='ew'||checkingFinJ)?'⏳':ended?'✅':'🔴'}</div>
        <div style={{fontSize:12,fontWeight:700}}>Fin de poste</div>
        {clk?.ew&&<div style={{fontSize:10,opacity:.8}}>{ft(clk.ew.ts)}</div>}
        {onBreak&&<div style={{fontSize:9,color:'var(--or)',marginTop:3}}>(terminer la pause d'abord)</div>}
      </button>
    </div>
    {clk&&<div className="card" style={{padding:'12px 14px'}}>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:8}}>Récapitulatif</div>
      <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:12,alignItems:'center'}}>
        <div style={{opacity:clk.sw?1:.4}}>🟢 <strong>Début</strong> : {clk.sw?ft(clk.sw.ts):'—'}</div>
        <div style={{opacity:clk.ew?1:.4}}>🔴 <strong>Fin</strong> : {clk.ew?ft(clk.ew.ts):'—'}</div>
        {breaks.length>0&&<div>☕ <strong>{breaks.length} pause{breaks.length>1?'s':''}</strong> ({fdur(totalBreakMs(clk))})</div>}
        {wMs&&<div style={{fontWeight:700,color:'var(--ok)'}}>⏱ Travail : {fdur(wMs)}</div>}
      </div>
      {encadre&&ended&&<div style={{marginTop:10,paddingTop:10,borderTop:'1px dashed var(--li)',display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,color:'var(--si)'}}>{tr(langue,{fr:'Code à transmettre à la Direction',es:'Código para enviar a la dirección'})} :</span>
        <span className="D" style={{fontSize:20,letterSpacing:'.1em',color:'var(--or)'}}>{clotureCode(sid,todayS)}</span>
      </div>}
    </div>}
    {cf&&<div className="mbk" onClick={()=>setCf(null)}><div style={{background:'var(--bg)',borderRadius:16,padding:22,maxWidth:340,width:'100%',margin:'0 14px'}} onClick={e=>e.stopPropagation()}><div className="D" style={{fontSize:19,marginBottom:7}}>Confirmer ?</div><div style={{fontSize:14,fontWeight:700,marginBottom:10}}>🕐 {ft(now())}</div><div style={{fontSize:11,color:cf.g?'var(--si)':'var(--dg)',marginBottom:14,fontWeight:cf.g?400:600}}>{cf.g?`📍 ±${cf.g.acc}m`:`⚠️ Position non disponible${cf.geoErr?' — '+(cf.geoErr.toLowerCase().includes('denied')||cf.geoErr.toLowerCase().includes('refus')?'autorisation refusée':'erreur GPS'):''}`}</div><div style={{display:'flex',gap:9}}><button onClick={()=>setCf(null)} className="btn bgh" style={{flex:1}}>Annuler</button><button onClick={confirm} className="btn bok" style={{flex:2}}>Valider</button></div></div></div>}
    {blocked&&<ClotureJournee mode="blocked" langue={langue} blockers={clotureBlockers} onClose={()=>setBlocked(false)}/>}
    {receipt&&<ClotureJournee mode="receipt" langue={langue} code={clotureCode(sid,todayS)} onClose={()=>setReceipt(false)}/>}
    {finJ&&<FinJourneeCheck rapports={finJ.rapports} apparts={finJ.apparts} envoye={finJSent} onEnvoyer={envoyerFinJ} onFermer={fermerFinJ}/>}
  </div>);
}

// Bandeau d'information affiché quand l'appli a refermé toute seule une pause restée
// ouverte (v130). Non bloquant, fermable, aucune action demandée : décision de Claire —
// le pointage sérieux reste la responsabilité du salarié, l'appli informe et décompte.
// Composant purement présentationnel, extrait pour être testable en montage.
function PauseAutoFermee({langue='fr',data,onClose}){
  if(!data)return null;
  const mins=Math.max(0,Math.round((new Date(data.end)-new Date(data.startEff))/60000));
  return(<div style={{background:'#fff4d6',border:'2px solid #e5c977',borderRadius:12,padding:'12px 16px',marginBottom:14,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
    <div style={{fontSize:26,lineHeight:1}}>☕</div>
    <div style={{flex:1,minWidth:210}}>
      <div className="D" style={{fontSize:14,color:'#7a5a00',marginBottom:2}}>{tr(langue,{fr:'Pause refermée automatiquement',es:'Pausa cerrada automáticamente'})}</div>
      <div style={{fontSize:12,color:'#7a5a00',lineHeight:1.5}}>
        {tr(langue,
          {fr:`Ta pause de ${ft(data.start)} est restée ouverte. Elle a été refermée à ${ft(data.end)} au démarrage de ${data.bien||'l\'appartement'} — ${mins} min décomptées.`,
           es:`Tu pausa de las ${ft(data.start)} quedó abierta. Se cerró a las ${ft(data.end)} al empezar ${data.bien||'el apartamento'} — ${mins} min descontados.`})}
        {data.borne&&' '+tr(langue,
          {fr:`Le décompte part de ${ft(data.startEff)}, fin de l'appartement précédent.`,
           es:`El descuento empieza a las ${ft(data.startEff)}, fin del apartamento anterior.`})}
      </div>
    </div>
    <button onClick={onClose} className="btn bgh sm" style={{fontSize:12}}>{tr(langue,{fr:'J\'ai compris',es:'Entendido'})}</button>
  </div>);
}

// ─── Alerte plein écran (v145) ─── Consigne critique posée par l'admin sur une tâche
// (champ `alerte`, distinct de `notes`). PAS de backdrop cliquable, PAS de croix en coin,
// PAS d'échappement clavier : la seule sortie est le bouton « J'ai compris ». Reprend le
// patron plein-cadre de ModeGuide.jsx (position fixed, au-dessus de tout, hors modale <M>
// générique) plutôt qu'une modale standard, pour un rendu impossible à manquer par erreur.
function AlerteTacheModal({task,bien,onClose}){
  if(!task?.alerte)return null;
  return(<div style={{position:'fixed',inset:0,zIndex:9999,background:'#8a5a00',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:24,textAlign:'center'}}>
    <div style={{fontSize:56,lineHeight:1,marginBottom:18}}>⚠️</div>
    <div style={{color:'white',fontWeight:700,fontSize:13,letterSpacing:'.05em',textTransform:'uppercase',opacity:.85,marginBottom:10}}>Consigne importante{bien?.name?` · ${bien.name}`:''}</div>
    <div style={{color:'white',fontSize:20,fontWeight:700,lineHeight:1.4,maxWidth:480,marginBottom:34,whiteSpace:'pre-wrap'}}>{task.alerte}</div>
    <button onClick={onClose} className="btn" style={{fontSize:15,fontWeight:700,padding:'12px 32px',background:'white',color:'#8a5a00',border:'none',borderRadius:10}}>J'ai compris, fermer</button>
  </div>);
}

function AptPunch({sid,st,biens,als,setAls,setAlsSynced,rep,onRep,onGuide,onComment,staff,tc,setTc,setTcSynced,pauseQR,setPauseQR,onDureeLock}){
  const todayS=today();
  // Éligibilité mode guidé formation : salarié opt-in (guideMode) ET bien avec
  // configuration ménage personnalisée (mr[]). Sinon → rapport classique.
  const me=(staff||[]).find(s=>s.id===sid);
  // Encadrement renforcé (Lot B) : on ne peut pas pointer le début d'un appart
  // sans avoir pointé son début de journée. Lecture de l'état LOCAL de tc (inclut
  // l'écriture optimiste de DailyPunch) → un aléa réseau ne re-bloque pas.
  const langue=me?.langue||'fr';
  const dayStarted=!!(tc||[]).find(c=>c.sid===sid&&c.date===todayS&&c.sw);
  const blockNoDay=!!me?.encadrementRenforce&&!dayStarted;
  // Degré d'imposition du salarié (off/optin/suggere/obligatoire) — rétrocompat gérée par guideImposition().
  const imposition=guideImposition(me);
  // Binôme : le mode guidé pas-à-pas suppose un seul salarié qui avance seul dans les étapes.
  // À deux sur la même prestation, il n'a plus de sens → toujours désactivé pour ces tâches,
  // quel que soit le réglage individuel du salarié (off/optin/suggéré/obligatoire).
  const guideEligible=(b,t)=>imposition!=='off'&&!!b?.mrOn&&(b?.mr||[]).length>0&&!t?.groupeBinome;
  // Le guide a-t-il déjà été choisi pour cette tâche ? On se base sur le rapport existant : seul
  // buildAndSaveRapport (chemin guidé) écrit un champ guideMode, jamais le rapport classique.
  const guideChosen=tid=>!!(rep||[]).find(r=>r.tid===tid)?.guideMode;
  // Faut-il router vers le guide (vs le rapport classique) à cet instant précis ?
  // - obligatoire : toujours, aucune alternative n'existe
  // - optin/suggéré : seulement si déjà choisi — sinon un vrai choix doit être présenté
  const guideActive=(t,b)=>guideEligible(b,t)&&(imposition==='obligatoire'||guideChosen(t.id));
  // Bouton(s) d'accès rapport/guide pour une tâche en cours — choix libre tant que rien n'est choisi
  // en optin/suggéré, verrouillé sur le guide en obligatoire ou une fois le guide entamé.
  const guideBtns=(t,b,cls,label)=>{
    const fw=label==='fin'?{fontWeight:700}:null;
    if(guideEligible(b,t)&&imposition!=='obligatoire'&&!guideChosen(t.id))return(
      <div style={{display:'inline-flex',gap:4}}>
        <button onClick={()=>onGuide(t)} className={cls} style={{fontSize:11,...fw}}>🧭 {label==='fin'?'Finir (guidé)':'Guidé'}</button>
        <button onClick={()=>onRep(t)} className={cls} style={{fontSize:11,...fw}}>📝 {label==='fin'?'Finir le rapport':'Rapport'}</button>
      </div>);
    const active=guideActive(t,b);
    return<button onClick={()=>active?onGuide(t):onRep(t)} className={cls} style={{fontSize:11,...fw}} title={active?'Ménage guidé pas-à-pas (formation)':'Remplir le rapport au fur et à mesure (sauvegardé en brouillon)'}>{active?(label==='fin'?'🧭 Finir (guidé)':'🧭 Guidé'):(label==='fin'?'📝 Finir le rapport':'📝 Rapport')}</button>;
  };
  const tasks=st.filter(t=>t.sid===sid&&t.date===todayS).sort((a,b)=>(a.sh-b.sh)||(a.sm-b.sm));
  const{req}=useGeo();
  const[ld,setLd]=useState(null);
  const[fichePrepa,setFichePrepa]=useState(null);
  // Encadrement renforcé (Lot D) : modale « pause oubliée ? » entre deux apparts.
  const[pauseAsk,setPauseAsk]=useState(null);
  // Bandeau d'information après fermeture automatique d'une pause ouverte (v130).
  // Non bloquant, fermable : il informe, il ne demande rien.
  const[autoClosed,setAutoClosed]=useState(null);
  // Journal Q/R (pauseQR7) : trace CHAQUE réponse à la modale « pause oubliée » (Oui ET Non),
  // avec la question posée (trou, trajet estimé, excédent) — indépendant de pauseAnom (qui ne
  // sert qu'au workflow de vérification des "Non" sur als7, inchangé). Read-before-write, non
  // bloquant : un échec ici ne doit jamais empêcher la réponse de l'utilisateur d'être prise en
  // compte (breaks[] ou pauseAnom, gérés séparément par l'appelant).
  const logPauseQR=async(ask,reponse)=>{
    const entry={id:'pq'+Date.now(),sid,date:todayS,ts:now(),alId:ask.alId,prevBienName:ask.prevBienName,newBienName:ask.newBienName,gapMin:ask.gapMin,expected:ask.expected,dejaMin:ask.dejaMin??0,excess:ask.excess,reponse};
    try{const base=await supaGet('pauseQR7')||[];const merged=[...base,entry];await supaSet('pauseQR7',merged);if(setPauseQR)setPauseQR(merged);}
    catch(e){console.error('[pauseQR] écriture échouée :',e.message);if(setPauseQR)setPauseQR(qr=>[...(qr||[]),entry]);}
  };
  // Oui → enregistre la pause suggérée (excédent) dans les breaks du jour.
  // tc7 (v134) : lecture fraîche avant écriture — c'est précisément le chemin qui a
  // perdu la pause de Lina le 04/08/2026. L'ancienne version se fiait au `tc` local
  // (potentiellement périmé) et, pire, ne faisait RIEN si l'enregistrement du jour
  // n'y était pas trouvé — silencieusement, alors que logPauseQR (lui, déjà en
  // lecture-avant-écriture) écrivait quand même sa trace. D'où le symptôme observé :
  // la question et la réponse visibles dans le journal, la pause absente du pointage.
  const onPauseOui=async()=>{
    if(!pauseAsk)return;
    const start=new Date(pauseAsk.prevEnd).toISOString();
    const end=new Date(pauseAsk.prevEnd+pauseAsk.excess*60000).toISOString();
    const newBreak={start,end,_declared:true};
    const applyBreak=c=>({...c,breaks:[...(c.breaks||[]),newBreak]});
    try{
      // makeNew absent volontairement : la modale « pause oubliée » ne peut apparaître
      // qu'après un ▶ Début d'appartement, qui exige lui-même que la journée ait déjà
      // commencé — un enregistrement du jour DOIT exister en base à ce stade.
      const{merged,changed}=await supaSetSafe('tc7',fresh=>applyTcUpdate(fresh,sid,todayS,{update:applyBreak}));
      if(!changed)throw new Error('aucun pointage du jour trouvé en base pour cette pause');
      if(setTcSynced)setTcSynced(merged);
    }catch(e){
      console.error('[tc7] onPauseOui écriture échouée, repli local :',e.message);
      const clk=(tc||[]).find(c=>c.sid===sid&&c.date===todayS);
      if(clk&&setTc)setTc(tc.map(c=>c.id===clk.id?applyBreak(c):c));
      else console.error('[tc7] onPauseOui : aucun pointage du jour trouvé même localement, pause perdue pour',sid,todayS);
    }
    logPauseQR(pauseAsk,'oui');
    setPauseAsk(null);
  };
  // Non → pose une anomalie sur la ligne de pointage (vérifiée par la Direction en SecRH).
  const onPauseNon=async()=>{
    if(!pauseAsk)return;
    const anom={gapMin:pauseAsk.gapMin,expected:pauseAsk.expected,excess:pauseAsk.excess,prevBienName:pauseAsk.prevBienName,ts:now()};
    const alId=pauseAsk.alId;
    try{const base=await supaGet('als7')||[];const merged=base.map(x=>x.id===alId?{...x,pauseAnom:anom}:x);await supaSet('als7',merged);setAls(merged);}
    catch(e){console.error('[pauseAnom] écriture échouée :',e.message);setAls(ls=>ls.map(x=>x.id===alId?{...x,pauseAnom:anom}:x));}
    logPauseQR(pauseAsk,'non');
    setPauseAsk(null);
  };
  const gL=(tid,type)=>als.find(l=>l.tid===tid&&l.type===type);
  const isMenage=type=>['full','quick','linge','correction'].includes(type);
  // Un ménage est "vraiment" terminé seulement si son rapport est soumis (champ sub non null)
  const hasReport=tid=>(rep||[]).some(r=>r.tid===tid&&r.sub);
  // Détecte s'il existe un ménage du jour avec fin pointée mais sans rapport soumis = "rapport en attente"
  // Exclut les tâches marquées noReport (rapport rendu optionnel par admin pour ce ménage précis)
  const pendingTasks=tasks.filter(t=>isMenage(t.type)&&!t.noReport&&gL(t.id,'end')&&!hasReport(t.id));
  const hasPending=pendingTasks.length>0;
  // Verrou anti-double-pointage (v184) : si un ménage est déjà en cours (Début pointé,
  // Fin pas encore pointée), bloquer le ▶ Début de tout autre ménage du jour.
  // Protège contre la race condition où la salariée clique deux Débuts avant de pointer
  // une Fin — non couvert par hasPending (qui ne se déclenche qu'après une Fin pointée).
  const inProgressTask=tasks.find(t=>isMenage(t.type)&&gL(t.id,'start')&&!gL(t.id,'end'));
  const hasInProgress=!!inProgressTask;
  // ─── Alerte plein écran (v145) ─── File des tâches du jour ayant une consigne `alerte`
  // et pas encore terminées (pas de pointage fin). Init au montage SEULEMENT (useState lazy) :
  // pas de mémorisation d'un état "déjà vu" persisté nulle part — l'onglet Tâches est démonté/
  // remonté à chaque changement d'onglet dans StaffUI (`{tab==='ptg'&&<AptPunch/>}`), donc l'objectif
  // de Claire (« réapparaît à chaque retour sur l'onglet, pas d'excuse ») est satisfait par le cycle
  // de vie React lui-même, sans code de persistance supplémentaire.
  const[alerteQueue,setAlerteQueue]=useState(()=>tasks.filter(t=>t.alerte&&!gL(t.id,'end')).map(t=>t.id));
  const alerteTask=alerteQueue.length?tasks.find(t=>t.id===alerteQueue[0]):null;
  const fermerAlerte=()=>setAlerteQueue(q=>q.slice(1));
  const rouvrirAlerte=t=>setAlerteQueue(q=>q.includes(t.id)?q:[t.id,...q]);
  // punch : read-before-write atomique sur als7 pour éviter les écrasements lors de collisions multi-session.
  // On lit la version courante en base, on y ajoute le nouveau pointage, on sauvegarde, puis on met à jour le state local.
  const punch=(t,type,opts={})=>{
    // Seuils qualité (v154, révisé le 11/08/2026) : le bouton Fin n'est plus jamais
    // désactivé à l'avance — le clic passe toujours par ici, qui recalcule le statut sur
    // `als` frais. Si la tâche est hors fourchette min/max du bien (tâches binôme
    // exclues), on n'écrit rien : on ouvre directement DureeSignalModal via onDureeLock,
    // avec un `retry` qui est la fonction punch() elle-même — envoyer le motif rappelle
    // punch(t,'end',{skipDureeCheck:true}) pour terminer réellement la tâche, sans dupliquer
    // la logique d'écriture ailleurs. `skipDureeCheck` permet ce second passage sans reboucler.
    if(type==='end'&&isMenage(t.type)&&!t.groupeBinome&&!opts.skipDureeCheck){
      const b0=biens.find(x=>x.id===t.bid);
      const ls0=gL(t.id,'start');
      const st0=ls0?dureeStatut(b0,t.type,(Date.now()-new Date(ls0.ts).getTime())/60000):null;
      if(st0){onDureeLock&&onDureeLock({task:t,bien:b0,sens:st0,retry:()=>punch(t,'end',{skipDureeCheck:true})});return;}
    }
    // Fin déjà pointée pour cette tâche : ne PAS réécrire (anti-doublon v186), mais ROUVRIR
    // le rapport s'il n'est pas encore soumis — c'est ce que la salariée cherche en re-cliquant
    // quand le rapport ne s'est pas ouvert assez vite (géoloc lente/refusée, cf. incident Justine
    // 05/09 : 3 Fin sur le Duplex, GÉO refusée). Même logique d'ouverture que le 1er passage
    // (ll.431-433). Si le rapport est déjà soumis → rien (tâche réellement terminée). N'affecte
    // pas le retry seuil qualité (rappelle punch AVANT qu'une Fin ait pu être écrite).
    if(type==='end'&&gL(t.id,'end')){
      setLd(null);
      if(isMenage(t.type)&&!hasReport(t.id)){
        const bR=biens.find(x=>x.id===t.bid);
        if(!guideEligible(bR,t))onRep(t);
        else if(imposition==='obligatoire'||guideChosen(t.id))onGuide(t);
        // optin/suggéré non choisi : le bandeau "rapport en attente" de la carte reste le point d'entrée
      }
      return;
    }
    setLd(t.id+type);const b=biens.find(x=>x.id===t.bid);req(async(g,err)=>{setLd(null);const ck=g&&b?geoOk(g,b):{ok:null};const newAl={id:'a'+Date.now(),sid,tid:t.id,bid:t.bid,type,ts:now(),geo:g||null,geoErr:err||null,ok:ck.ok,dist:ck.d};
    // Chantier B (01/08/2026) : écriture locale AVANT le réseau — voir GerantUI.jsx
    // pour le détail (même correctif, dupliqué à l'identique dans les deux copies de punch()).
    await enqueue({key:'als7',recordId:newAl.id,payload:newAl,sid,authorLabel:'',fin:true});
    try{const base=await supaGet('als7')||[];const merged=[...base.filter(x=>x.id!==newAl.id),newAl];await supaSet('als7',merged);setAlsSynced(merged);confirmByRecordIds('als7',[newAl.id]);}catch(e){console.error('[punch] Écriture als7 échouée, retry en cours :',e.message);setAlsSynced([...als,newAl]);syncBus.dispatchEvent(new CustomEvent('error',{detail:{key:'als7',message:e.message,attempt:1,op:'write'}}));const _punchRetry=(attempt)=>{const delay=Math.min(2000*Math.pow(2,attempt-1),30000);setTimeout(async()=>{try{const base2=await supaGet('als7')||[];const merged2=[...base2.filter(x=>x.id!==newAl.id),newAl];await supaSet('als7',merged2);setAlsSynced(merged2);confirmByRecordIds('als7',[newAl.id]);syncBus.dispatchEvent(new CustomEvent('ok',{detail:{key:'als7'}}));}catch(e2){console.error('[punch] Retry',attempt,'échoué :',e2.message);syncBus.dispatchEvent(new CustomEvent('error',{detail:{key:'als7',message:e2.message,attempt:attempt+1,op:'write'}}));_punchRetry(attempt+1);}},delay);};_punchRetry(1);}if(type==='end'&&isMenage(t.type)){
    // ─── notif : menage_termine (vers admin/direction) ───
    const moi=(staff||[]).find(s=>s.id===sid);
    notifEventBus.emit('menage_termine',{
      taskId:t.id, taskType:t.type, bienName:b?.name,
      fromName:moi?.name||fname(moi)||'Salarié(e)',
      summary:`Ménage terminé à ${b?.name||'?'} par ${moi?.name||fname(moi)||'?'}`
    });
    if(!guideEligible(b,t))onRep(t);
    else if(imposition==='obligatoire'||guideChosen(t.id))onGuide(t);
    // sinon (optin/suggéré, rien choisi encore) : on n'ouvre rien d'office, le salarié choisira
    // via le bandeau "rapport en attente" sur la carte de la tâche.
  }else if(type==='end'){
    onComment(t);
  }else if(type==='start'&&isMenage(t.type)&&imposition==='obligatoire'&&guideEligible(b,t)){
    // Formation obligatoire : le Début ouvre directement le guide, aucun bouton à taper.
    onGuide(t);
  }
  if(type==='start'){
    // ── Pause ouverte : fermeture automatique au démarrage d'un appartement (v130) ──
    // Borne basse du décompte : dernière fin d'appartement antérieure au ▶ Début.
    // Sans elle, une pause ouverte pendant l'appartement précédent ferait décompter
    // du temps où la salariée était pointée en train de travailler.
    const nowTs=new Date(newAl.ts).getTime();
    const ends=(als||[]).filter(l=>l.sid===sid&&l.type==='end'&&new Date(l.ts).getTime()<=nowTs);
    const prevEnd=ends.length?ends.reduce((m,l)=>new Date(l.ts).getTime()>new Date(m.ts).getTime()?l:m).ts:null;
    // tc7 (v134) : lecture fraîche avant écriture (même raison qu'onPauseOui plus haut).
    // `closeIfOpen` respecte la convention « même référence = rien à faire ».
    const closeIfOpen=c=>{
      const closed=fermerPauseOuverte(c.breaks||[],prevEnd,newAl.ts);
      return closed===(c.breaks||[])?c:{...c,breaks:closed};
    };
    // Meilleure estimation connue des pauses du jour pour la détection Lot D ci-dessous,
    // au cas où l'écriture réseau échouerait avant de la mettre à jour.
    let finalBreaks=(tc||[]).find(c=>c.sid===sid&&c.date===todayS)?.breaks||[];
    try{
      const{merged,changed}=await supaSetSafe('tc7',fresh=>applyTcUpdate(fresh,sid,todayS,{update:closeIfOpen}));
      finalBreaks=merged.find(c=>c.sid===sid&&c.date===todayS)?.breaks||finalBreaks;
      if(changed&&setTcSynced){
        setTcSynced(merged);
        const bc=finalBreaks[finalBreaks.length-1];
        if(bc)setAutoClosed({start:bc._startOrig||bc.start,startEff:bc.start,end:bc.end,borne:!!bc._startOrig,bien:b?.name||''});
      }
    }catch(e){
      console.error('[tc7] fermeture auto pause échouée, repli local :',e.message);
      const clkLocal=(tc||[]).find(c=>c.sid===sid&&c.date===todayS);
      if(clkLocal&&setTc){
        const updated=closeIfOpen(clkLocal);
        if(updated!==clkLocal){
          setTc(tc.map(c=>c.id===clkLocal.id?updated:c));
          finalBreaks=updated.breaks;
          const bc=finalBreaks[finalBreaks.length-1];
          setAutoClosed({start:bc._startOrig||bc.start,startEff:bc.start,end:bc.end,borne:!!bc._startOrig,bien:b?.name||''});
        }
      }
    }
    // Encadrement renforcé : détecte un trou anormal depuis l'appart précédent →
    // propose de saisir la pause oubliée, ou pose une anomalie. Le calcul déduit
    // les pauses déjà déclarées dans le trou, celle qu'on vient de fermer comprise.
    if(me?.encadrementRenforce){
      const d=detectPauseOubliee({als,sid,todayS,newAl,biens,breaks:finalBreaks});
      if(d)setPauseAsk(d);
    }
  }});};
  // Auto-punch Fin en obligatoire : dès que le guide vient de soumettre son rapport (fin=true) pour une
  // tâche encore "en cours" (Début pointé, Fin pas encore pointée), on pointe la fin automatiquement —
  // pas de bouton "Fin" en mode formation. Garde-fou par ref : punch() est asynchrone (géoloc + écriture
  // réseau), le composant peut re-render pendant ce temps sans que `le` soit encore posé ; sans le ref on
  // redéclencherait un punch à chaque re-render tant que l'écriture n'est pas retombée dans `als`.
  const autoEndedRef=useRef(new Set());
  useEffect(()=>{
    if(imposition!=='obligatoire')return;
    tasks.forEach(t=>{
      if(!isMenage(t.type))return;
      if(!gL(t.id,'start')||gL(t.id,'end'))return;
      if(autoEndedRef.current.has(t.id))return;
      if(rep.find(r=>r.tid===t.id)?.sub){autoEndedRef.current.add(t.id);punch(t,'end');}
    });
    // eslint: dépendance volontairement limitée à `rep` — c'est la soumission du rapport (fin=true)
    // qui doit déclencher l'auto-punch, pas chaque changement de `tasks`/`als`.
  },[rep]);
  return(<div>
    {/* Alerte plein écran (v145) : consigne critique posée par l'admin sur la tâche. Fermeture
        manuelle obligatoire, aucune fermeture automatique/backdrop. Voir déclenchement ci-dessus. */}
    {alerteTask&&<AlerteTacheModal task={alerteTask} bien={biens.find(b=>b.id===alerteTask.bid)} onClose={fermerAlerte}/>}
    {/* Pause refermée automatiquement au démarrage d'un appartement (v130) — informatif */}
    {autoClosed&&<PauseAutoFermee langue={langue} data={autoClosed} onClose={()=>setAutoClosed(null)}/>}
    {/* Encadrement renforcé (Lot B) : bandeau bilingue si la journée n'est pas encore ouverte */}
    {blockNoDay&&tasks.length>0&&(
      <div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:12,padding:'14px 18px',marginBottom:14,display:'flex',gap:14,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{fontSize:32,lineHeight:1}}>⬆️</div>
        <div style={{flex:1,minWidth:220}}>
          <div className="D" style={{fontSize:15,color:'var(--dg)',marginBottom:3}}>{tr(langue,{fr:'Pointe d\'abord ton début de journée',es:'Ficha primero tu inicio de jornada'})}</div>
          <div style={{fontSize:12,color:'#8a0000',lineHeight:1.5}}>{tr(langue,{fr:'Tu ne peux pas commencer un appartement tant que ton « Début de poste » n\'est pas pointé (juste au-dessus).',es:'No puedes empezar un apartamento mientras no hayas fichado tu « Inicio de jornada » (justo arriba).'})}</div>
        </div>
      </div>
    )}
    {/* Bannière globale : un ou plusieurs rapports de ménage non soumis bloquent l'ouverture d'un autre pointage */}
    {hasPending&&(
      <div style={{background:'#fff4d6',border:'2px solid #e5c977',borderRadius:12,padding:'14px 18px',marginBottom:14,display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
        <div style={{fontSize:32,lineHeight:1}}>📝</div>
        <div style={{flex:1,minWidth:240}}>
          <div className="D" style={{fontSize:15,color:'#7a5a00',marginBottom:4}}>{pendingTasks.length===1?'Rapport en attente':`${pendingTasks.length} rapports en attente`}</div>
          <div style={{fontSize:12,color:'#7a5a00',lineHeight:1.5}}>
            Tu dois <strong>soumettre le rapport de fin de ménage</strong> avant de pouvoir commencer une autre intervention :
            <ul style={{margin:'6px 0 0 18px',padding:0,lineHeight:1.6}}>
              {pendingTasks.map(t=>{const b=biens.find(x=>x.id===t.bid);return(<li key={t.id}><strong>{b?.name||'?'}</strong> — clique sur le bouton orange <em>📝 Finir le rapport</em> sur cet appartement.</li>);})}
            </ul>
          </div>
        </div>
      </div>
    )}
    <div className="D" style={{fontSize:17,marginBottom:10}}>Tâches du jour</div>
    {tasks.length===0?<div className="empty">Aucune tâche</div>:(
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {tasks.map(t=>{const b=biens.find(x=>x.id===t.bid),tt=TT[t.type],ls=gL(t.id,'start'),le=gL(t.id,'end');const menage=isMenage(t.type);
          // Standards propriétaire encore sur-visibles (SPEC §12.5) : compte dérivé de bien.mr[], pas un champ de tâche.
          const nbStdVisi=menage&&b?.mrOn?(b.mr||[]).reduce((n,r)=>n+(r.items||[]).filter(it=>it.std&&it.std.survisible!==false).length,0):0;
          // État du rapport pour ce ménage
          const reportDone=menage&&hasReport(t.id);
          // reportPending = ménage terminé sans rapport soumis. Exclut les tâches noReport (rapport rendu optionnel)
          const reportPending=menage&&le&&!reportDone&&!t.noReport;
          // Construit l'URL d'itinéraire Google Maps : préfère les coordonnées GPS si disponibles, sinon l'adresse
          const mapDest=b?.lat&&b?.lng?`${b.lat},${b.lng}`:[b?.addr,b?.zip,b?.city].filter(Boolean).join(' ');
          const mapUrl=mapDest?`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapDest)}&travelmode=driving`:null;
          // ─── Binôme : bandeau plein, toujours visible (pas caché derrière un clic) ───
          // Objectif explicite de Claire : impossible pour la salariée de dire "j'avais pas vu".
          const partenaireTache=t.groupeBinome?st.find(x=>x.groupeBinome===t.groupeBinome&&x.id!==t.id):null;
          const nomPartenaire=partenaireTache?fnameShort((staff||[]).find(s=>s.id===partenaireTache.sid)):null;
          return(<div key={t.id} className="card" style={{padding:0,overflow:'hidden',borderLeft:`4px solid ${t.groupeBinome?'#7F77DD':(reportPending?'#e5c977':tt?.c||'var(--or)')}`,background:reportPending?'#fffaeb':undefined}}>
          {t.groupeBinome&&<div style={{background:'#EEEDFE',padding:'7px 15px',display:'flex',alignItems:'center',gap:7,fontSize:12,fontWeight:700,color:'#3C3489'}}>
            <span>🤝 Tâche en binôme{nomPartenaire?` avec ${nomPartenaire}`:''}</span>
            {menage&&<span style={{fontWeight:400,opacity:.9}}>· {t.noReport?`rapport rempli par ${nomPartenaire||'le binôme'}`:'rapport à votre charge'}</span>}
          </div>}
          {/* Alerte plein écran (v145) : bandeau visible + cliquable, permet de relire la
              consigne à tout moment sans attendre le déclenchement automatique. */}
          {t.alerte&&<div onClick={()=>rouvrirAlerte(t)} style={{background:'#fff4e0',padding:'7px 15px',display:'flex',alignItems:'center',gap:7,fontSize:12,fontWeight:700,color:'#8a5a00',cursor:'pointer'}}>
            <span>⚠️ Consigne importante — appuie pour relire</span>
          </div>}
          <div style={{padding:'12px 15px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8,marginBottom:7}}>
            <div><div style={{fontWeight:700,fontSize:13}}>{tt?.e} {tt?.l}{!tt?.noBid&&b?.name?' · '+b.name:''}{nbStdVisi>0&&<span style={{marginLeft:6,fontSize:10,fontWeight:700,color:'var(--or)',background:'#fff4e8',padding:'2px 7px',borderRadius:999}} title="Standards propriétaire sur-visibles à respecter">⭐ {nbStdVisi} à respecter</span>}</div><div style={{fontSize:11,color:'var(--si)'}}>{String(t.sh).padStart(2,'0')}:{String(t.sm).padStart(2,'0')} ({t.dm}min)</div></div>
            <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
              {mapUrl&&!le&&<a href={mapUrl} target="_blank" rel="noopener noreferrer" className="btn bgh sm" style={{fontSize:11,textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4}} title="Ouvrir l'itinéraire dans Google Maps">🗺️ Itinéraire</a>}
              {menage&&<button onClick={()=>setFichePrepa(b)} className="btn bgh sm" style={{fontSize:11}}>📋 Prépa</button>}
              {ls&&!le&&menage&&<div style={{display:'inline-flex',flexDirection:'column',alignItems:'center',gap:1}}>
                {guideBtns(t,b,'btn bgh sm','')}
                {imposition==='suggere'&&guideEligible(b,t)&&!guideChosen(t.id)&&<span style={{fontSize:9,color:'var(--or)',fontWeight:600,marginTop:2,textAlign:'center'}}>💡 Suggéré pour cette prestation</span>}
                {t.noReport&&<span style={{fontSize:9,color:'var(--si)',fontStyle:'italic'}}>optionnel</span>}
              </div>}
              {!ls
                ?<button onClick={()=>punch(t,'start')} className="btn bmn sm" disabled={ld===t.id+'start'||hasPending||blockNoDay||(hasInProgress&&inProgressTask.id!==t.id)} title={blockNoDay?tr(langue,{fr:'Pointe d\'abord ton début de journée',es:'Ficha primero tu inicio de jornada'}):(hasPending?'Termine d\'abord le rapport en attente':(hasInProgress&&inProgressTask.id!==t.id)?`${biens.find(x=>x.id===inProgressTask.bid)?.name||'Un autre ménage'} est déjà en cours — termine-le d\'abord`:'')} style={(hasPending||blockNoDay||(hasInProgress&&inProgressTask.id!==t.id))?{opacity:.4,cursor:'not-allowed'}:{}}>▶ Début</button>
                :!le
                  // Bloquer le bouton Fin si un AUTRE ménage a son rapport en attente
                  // (mais autoriser celui de la tâche en cours, sinon impossible de la finir).
                  // Le seuil qualité (v154) ne désactive plus le bouton : punch() recalcule
                  // le statut au clic et ouvre DureeSignalModal lui-même si besoin — aucun
                  // texte, aucun chiffre n'est affiché avant le clic (décision Claire inchangée).
                  ?(()=>{const blockEnd=hasPending&&!pendingTasks.some(p=>p.id===t.id);
                    return(<button onClick={()=>punch(t,'end')} className="btn bor sm" disabled={ld===t.id+'end'||blockEnd} title={blockEnd?'Termine d\'abord le rapport en attente sur un autre appart':''} style={blockEnd?{opacity:.4,cursor:'not-allowed'}:{}}>⏹ {menage&&!t.noReport?'Fin+Rapport':'Fin'}</button>);
                  })()
                  :reportPending
                    ?guideBtns(t,b,'btn bor sm',/*label=*/ 'fin')
                    :<span className="pill pok">✓ Terminé</span>
              }
              {le&&menage&&reportDone&&<button onClick={()=>onRep(t)} className="btn bgh sm" title="Voir / modifier le rapport"><Ic n="rp" c="w-3.5 h-3.5"/></button>}
              {le&&!menage&&<button onClick={()=>onComment(t)} className="btn bgh sm" style={{fontSize:11}}>💬</button>}
            </div>
          </div>
          {(ls||le)&&<div style={{display:'flex',gap:10,fontSize:11}}>{ls&&<span style={{color:'var(--ok)',fontWeight:600}}>▶ {ft(ls.ts)} {ls.ok===false?'⚠':''}</span>}{le&&<span style={{color:'var(--dg)',fontWeight:600}}>⏹ {ft(le.ts)} {le.ok===false?'⚠':''}</span>}</div>}
          </div>
        </div>);})}
      </div>
    )}
    {fichePrepa&&<M onC={()=>setFichePrepa(null)} title={`📋 Fiche préparation · ${fichePrepa?.name}`} col="var(--mn)" ch={<FichePrepaModal bien={fichePrepa} onClose={()=>setFichePrepa(null)}/>}/>}
    {pauseAsk&&<PauseOubliee langue={langue} data={pauseAsk} onOui={onPauseOui} onNon={onPauseNon}/>}
  </div>);
}

export { DailyPunch, AptPunch, PauseAutoFermee };

